import React, { useState, useEffect } from 'react';
import { partnerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Check, X, Phone, Mail, Building2, Eye, TrendingUp } from 'lucide-react';
import Partner360View from '../../components/partner/Partner360View';

const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [viewDialog, setViewDialog] = useState({ 
    open: false, 
    partnerId: null, 
    data: null, 
    loading: false,
    dateRange: { startDate: null, endDate: null }
  });

  useEffect(() => {
    loadPartners();
  }, [activeTab]);

  const loadPartners = async () => {
    setLoading(true);
    try {
      const status = activeTab === 'pending' ? 'pending' : activeTab === 'approved' ? 'approved' : undefined;
      const response = await partnerAPI.list(status);
      setPartners(response.data);
    } catch (error) {
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (partnerId) => {
    try {
      await partnerAPI.approve(partnerId);
      toast.success('Partner approved successfully');
      loadPartners();
    } catch (error) {
      toast.error('Failed to approve partner');
    }
  };

  const handleViewDetails = async (partnerId, dateRange = {}) => {
    setViewDialog({ open: true, partnerId, data: null, loading: true, dateRange });
    try {
      const response = await partnerAPI.get360View(partnerId, dateRange);
      setViewDialog({ open: true, partnerId, data: response.data, loading: false, dateRange });
    } catch (error) {
      toast.error('Failed to load partner details');
      setViewDialog({ open: false, partnerId: null, data: null, loading: false, dateRange: {} });
    }
  };

  const handleDateRangeChange = (newDateRange) => {
    if (viewDialog.partnerId) {
      handleViewDetails(viewDialog.partnerId, newDateRange);
    }
  };

  const getTierBadge = (tier) => {
    const styles = {
      'bronze': 'tier-bronze',
      'silver': 'tier-silver',
      'gold': 'tier-gold',
      'platinum': 'tier-platinum',
    };
    return styles[tier] || 'tier-bronze';
  };

  const pendingCount = partners.filter(p => !p.is_approved).length;
  const approvedCount = partners.filter(p => p.is_approved).length;

  return (
    <div className="space-y-6" data-testid="partners-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Channel Partners</h1>
        <p className="text-muted-foreground">Manage your channel partner network</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-partners">
            All ({partners.length})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending-partners">
            Pending ({pendingCount})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved-partners">
            Approved ({approvedCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-28" />
              ))}
            </div>
          ) : partners.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No partners found</p>
                <p className="text-muted-foreground">
                  {activeTab === 'pending' 
                    ? 'No pending partner applications' 
                    : 'Partners will appear here when they register'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {partners.map((partner) => (
                <Card key={partner.id} className="summary-card">
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-lg">
                            {partner.user?.name || 'Partner'}
                          </h3>
                          <Badge className={getTierBadge(partner.tier)}>
                            {partner.tier || 'Bronze'}
                          </Badge>
                          {partner.is_approved ? (
                            <Badge className="status-approved">Approved</Badge>
                          ) : (
                            <Badge className="status-pending">Pending</Badge>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          {partner.user?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {partner.user.phone}
                            </span>
                          )}
                          {partner.user?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {partner.user.email}
                            </span>
                          )}
                          {partner.company_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3 w-3" />
                              {partner.company_name}
                            </span>
                          )}
                        </div>
                        
                        {partner.assigned_projects?.length > 0 && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {partner.assigned_projects.length} projects assigned
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {!partner.is_approved && (
                          <Button 
                            onClick={() => handleApprove(partner.id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`btn-approve-${partner.id}`}
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                        )}
                        <Button 
                          variant="outline"
                          onClick={() => handleViewDetails(partner.id)}
                          data-testid={`btn-view-${partner.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          360° View
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Partner 360 View Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ ...viewDialog, open })}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          {viewDialog.loading ? (
            <div className="space-y-4 py-8">
              <Skeleton className="h-20 w-full" />
              <div className="grid grid-cols-6 gap-3">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24" />)}
              </div>
              <Skeleton className="h-64 w-full" />
            </div>
          ) : viewDialog.data ? (
            <Partner360View 
              data={viewDialog.data} 
              dateRange={viewDialog.dateRange}
              onDateRangeChange={handleDateRangeChange}
              onClose={() => setViewDialog({ open: false, partnerId: null, data: null, loading: false, dateRange: {} })}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersPage;
